import ApplicationController from './application_controller';

export default class extends ApplicationController {
  static targets = [
    'form',
    'invoiceTable',
    'amount',
    'paymentType',
    'hiddenPaymentFields',
    'accountNumber',
    'payableType',
    'reuseDetailsFromId',
    'selectedPatientInvoice',
    'summaryCount',
    'summaryTotal',
    'preview',
    'selectAll',
    'submit',
  ];

  static values = {
    options: Object, // optional JSON config from HTML
  };

  // Payment type used when a credit card PPM is selected
  static CARD_PAYMENT_TYPES = ['credit_card'];
  static FACILITY_PAYABLE_TYPE = 'FacilityInvoice';

  /**
   * Stimulus lifecycle hook.
   * Initializes state, formats existing amount fields, and rebuilds selection state when navigating back via Turbo.
   */
  connect() {
    this.selectedPatientInvoices = new Map();
    window.isDirty = window.isDirty || false;
    // Auto-format all amounts on initial load
    this.amountTargets.forEach((input) => {
      if (input.value.trim() !== '') {
        input.value = this.formatAmount(input.value);
      }
    });
    // Rebuild selection state if coming back via browser history (give the checkboxes a bit to build)
    setTimeout(() => this.refreshSelectedPayments(), 150);
  }

  amountChanged(event) {
    const amount = event.target;
    amount.value = this.formatAmount(amount.value);
    this.refreshSelectedPayments();
  }

  /**
   * Handles radio button (payment method) changes.
   * Filters the payment type dropdown to show only relevant options for the selected PPM.
   */
  change(event) {
    const radio = event.target;
    const { invoiceId, paymentMethodType } = radio.dataset;
    if (paymentMethodType) {
      this.filterPaymentTypes(invoiceId, paymentMethodType);
    }
    this.refreshSelectedPayments();
  }

  /**
   * Finds a single target for a given invoice ID.
   */
  find(invoiceId, name) {
    return this[`${name}Targets`].find((x) => x.dataset.invoiceId === invoiceId);
  }

  /**
   * Finds all targets for a given invoice ID.
   */
  findAll(invoiceId, name) {
    return this[`${name}Targets`].filter((x) => x.dataset.invoiceId === invoiceId);
  }

  /**
   * Initializes DataTables when the invoice table appears.
   * Supports both "new" and "preview" layouts by inspecting column count.
   */
  invoiceTableTargetConnected(table) {
    const $ = window.jQuery || window.$;
    if (this.dt) this.dt.destroy();
    // Used by two views; New and Preview.
    const columnCount = table.querySelectorAll('thead th').length;
    let columnDefs;
    if (columnCount === 4) {
      columnDefs = [
        { targets: 0, orderable: false },
        { targets: 1, className: 'fast-pay-invoice-column' },
        { targets: 2, className: 'fast-pay-payment-type-column', orderable: false },
        { targets: 3, className: 'fast-pay-amount-column' },
      ];
    } else {
      columnDefs = [
        { targets: 0, className: 'fast-pay-invoice-column-preview' },
        { targets: 1, className: 'fast-pay-payment-type-column' },
        { targets: 2, className: 'fast-pay-amount-column' },
      ];
    }

    const defaults = {
      responsive: true,
      order: [[columnCount === 4 ? 1 : 0, 'asc']],
      pageLength: 25,
      columnDefs,
      autoWidth: false,
      drawCallback: () => {
        // this.$ is the jQuery instance that has selectize attached — see paymentTypeTargetConnected.
        // window.$ and window.jQuery are different instances that do NOT have selectize.
        // On the initial draw (page 1) this.$ isn't set yet; turbo:load handles page 1.
        // On subsequent draws (pagination), this.$ is set and we initialize any bare selects.
        if (!this.$) return;
        this.$(table).find('.select.filterable-text-with-create:not(.selectized)').selectize({
          create: true,
          sortField: 'text',
        });
      },
    };

    const config = { ...defaults, ...(this.optionsValue || {}) };
    this.dt = $(table).DataTable(config);
  }

  /**
   * Cleans up DataTable instance when controller disconnects.
   */
  invoiceTableTargetDisconnected() {
    if (this.dt) {
      this.dt.destroy();
      this.dt = null;
    }
  }

  /**
   * Builds a normalized payment payload for a given invoice. Used when constructing hidden inputs for form submission.
   */
  paymentDetails(invoiceId) {
    const amount = this.find(invoiceId, 'amount')?.value;
    const paymentType = this.find(invoiceId, 'paymentType')?.selectize?.getValue();
    const payableType = this.find(invoiceId, 'payableType')?.value;
    const reuseDetailsFromType = payableType === this.constructor.FACILITY_PAYABLE_TYPE ? 'FacilityInvoicePayment' : 'PatientPaymentMethod';
    const reuseDetailsFromId = this.findAll(invoiceId, 'reuseDetailsFromId').find(
      (reuseDetailsFrom) => reuseDetailsFrom.checked
    )?.value;
    const accountNumber = this.find(invoiceId, 'accountNumber').value;

    const details = {
      payable_id: invoiceId,
      payable_type: payableType,
      amount,
      reuse_details_from_id: reuseDetailsFromId,
      reuse_details_from_type: reuseDetailsFromType,
      payment_type: paymentType,
      account_number: accountNumber,
    };

    for (const key in details) {
      if (details[key] === undefined) {
        delete details[key];
      }
    }

    return details;
  }

  /**
   * Hook for Selectize-backed payment type dropdowns. Ensures change events propagate into Stimulus.
   */
  paymentTypeTargetConnected(paymentType) {
    if (!paymentType.selectize) {
      // Selectize hasn't initialized yet. Retry shortly.
      setTimeout(this.paymentTypeTargetConnected.bind(this, paymentType), 100);
      return;
    }

    // Acquire the jQuery constructor from the first Selectize instance we find.
    if (!this.$) {
      this.$ = paymentType.selectize.$input.constructor;
    }

    // Bind to the Selectize change event so updates propagate into Stimulus.
    paymentType.selectize.on('change', this.typeChanged.bind(this, paymentType));

    // Apply initial filtering based on any pre-selected radio button for this invoice
    const { invoiceId } = paymentType.dataset;
    if (invoiceId) {
      const selectedRadio = this.findAll(invoiceId, 'reuseDetailsFromId').find((r) => r.checked);
      if (selectedRadio?.dataset.paymentMethodType) {
        this.filterPaymentTypes(invoiceId, selectedRadio.dataset.paymentMethodType);
      }
    }
  }

  /**
   * Hook for Selectize-backed account number dropdowns. Ensures change events propagate into Stimulus.
   */
  accountNumberTargetConnected(accountNumber) {
    if (!accountNumber.selectize) {
      setTimeout(this.accountNumberTargetConnected.bind(this, accountNumber), 100);
      return;
    }

    accountNumber.selectize.on('change', () => this.refreshSelectedPayments());
  }

  /**
   * Selects or deselects all invoices currently visible in the table.
   */
  selectAll(event) {
    const offPageElements = [];
    const onScreenIds = new Set(this.selectedPatientInvoiceTargets.map((x) => x.dataset.invoiceId));
    const { checked } = event.target;

    this.dt.rows({ search: 'applied' }).every(function () {
      const row = $(this.node());
      row.find('[data-fast-pay-target]').each((index, element) => {
        // Skip disabled checkboxes (invoices without an available payment method)
        if (element.dataset.fastPayTarget === 'selectedPatientInvoice' && !element.disabled) {
          element.checked = checked;
        }
        // Grab the off screen ones
        if (!onScreenIds.has(element.dataset.invoiceId)) offPageElements.push(element.cloneNode());
      });
    });

    // Append the off page elements so we can use the same code flow
    offPageElements.forEach((element) => this.formTarget.appendChild(element));
    // Trigger the toggle event to prep them for submission
    this.selectedPatientInvoiceTargets.forEach((x) => this.toggleSelectPatientInvoice(x, false));
    // Update all of the data at one time instead of each invoice individually
    this.updateHiddenPaymentFields();
    this.validateSubmitState();
    // Off page elements are no longer needed
    offPageElements.forEach((x) => x.remove());
  }

  /**
   * Wrapper for checkbox change events.
   */
  toggle(event) {
    this.refreshSelectedPayments();
  }

  /**
   * Syncs Selectize changes into the internal model.
   */
  typeChanged(originalElement) {
    originalElement.value = originalElement.selectize?.getValue();
    this.refreshSelectedPayments();
  }

  /**
   * Adds or removes an invoice from the selected set.
   */
  toggleSelectPatientInvoice(selectPatientInvoice, updateHidden = true) {
    const { invoiceId } = selectPatientInvoice.dataset;
    if (invoiceId) {
      if (selectPatientInvoice.checked) {
        this.selectedPatientInvoices.set(invoiceId, this.paymentDetails(invoiceId));
      } else {
        this.selectedPatientInvoices.delete(invoiceId);
      }
      // Update form fields used to submit the payment information
      if (updateHidden) {
        this.updateHiddenPaymentFields();
      }
      // if something is checked
      window.isDirty = true;
    }
  }

  /**
   * Rebuilds hidden inputs used for form submission. Also recalculates totals and selected count.
   */
  updateHiddenPaymentFields() {
    if (this.hasHiddenPaymentFieldsTarget) {
      this.hiddenPaymentFieldsTarget.innerHTML = '';

      [...this.selectedPatientInvoices.values()].forEach((details, index) => {
        let input;
        for (const [field, value] of Object.entries(details)) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = `fast_pay[payments_attributes][${index}][${field}]`;
          input.value = value;
          this.hiddenPaymentFieldsTarget.appendChild(input);
        }
      });
    }

    const total = Array.from(this.selectedPatientInvoices.values()).reduce(
      (sum, payment) => sum + Number(payment.amount.replace(/,/g, '')),
      0
    );

    this.summaryTotalTarget.innerHTML = `$${total.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;

    this.summaryCountTarget.innerHTML = this.selectedPatientInvoices.size;
  }

  /**
   * Formats raw numeric input into a currency string.
   */
  formatAmount(raw) {
    return [raw.replace(/\D/g, '').padStart(3, '0')]
      .map((s) => [Number(s.slice(0, -2)), s.slice(-2)])[0]
      .join('.');
  }

  /**
   * Filters the payment type Selectize dropdown based on the selected PPM type.
   * Credit card PPMs show only credit_card/debit_card options; non-card PPMs show all options.
   */
  filterPaymentTypes(invoiceId, paymentMethodType) {
    const paymentTypeEl = this.find(invoiceId, 'paymentType');
    if (!paymentTypeEl?.selectize) return;

    const selectize = paymentTypeEl.selectize;

    // Store original options on first call so we can restore them later.
    // Only keep value/text to avoid Selectize internal state ($order, etc.) from leaking across rebuilds.
    if (!this._originalPaymentTypeOptions) this._originalPaymentTypeOptions = {};
    if (!this._originalPaymentTypeOptions[invoiceId]) {
      this._originalPaymentTypeOptions[invoiceId] = Object.values(selectize.options).map((opt) => ({
        value: opt.value,
        text: opt.text,
      }));
    }

    const allOptions = this._originalPaymentTypeOptions[invoiceId];
    const isCard = paymentMethodType === 'credit_card';
    const cardTypes = this.constructor.CARD_PAYMENT_TYPES;

    // Card PPM → only card types; non-card PPM → exclude card types
    const validOptions = isCard
      ? allOptions.filter((opt) => cardTypes.includes(opt.value))
      : allOptions.filter((opt) => !cardTypes.includes(opt.value));

    // Rebuild the Selectize options.
    // clear() deselects the current item first — clearOptions() skips selected items.
    const currentValue = selectize.getValue();
    selectize.clear(true);
    selectize.clearOptions();
    validOptions.forEach((opt) => selectize.addOption(opt));
    selectize.refreshOptions(false);

    // Restore the previous value if it still makes sense for the selected PPM,
    // otherwise default to the first valid option.
    const currentStillValid = validOptions.find((opt) => opt.value === currentValue);
    const currentIsCard = cardTypes.includes(currentValue);
    if (currentStillValid && (isCard || !currentIsCard)) {
      selectize.setValue(currentValue, true);
    } else {
      selectize.setValue(validOptions[0]?.value, true);
    }
  }

  /**
   * Rebuilds the selected payment state based on currently checked invoices.
   * This method is used when amounts or related fields change without toggling
   * the checkbox itself, ensuring totals and hidden fields stay in sync.
   */
  refreshSelectedPayments() {
    // Only update entries for checkboxes visible in the DOM.
    // Off-page DataTable rows are removed from the DOM, so clearing the entire
    // Map would lose selections made on other pages.
    this.selectedPatientInvoiceTargets.forEach((checkbox) => {
      const { invoiceId } = checkbox.dataset;
      if (checkbox.checked) {
        this.selectedPatientInvoices.set(invoiceId, this.paymentDetails(invoiceId));
      } else {
        this.selectedPatientInvoices.delete(invoiceId);
      }
    });
    window.isDirty = this.selectedPatientInvoices.size > 0;
    this.updateHiddenPaymentFields();
    this.validateSubmitState();
  }

  /**
   * Enables Submit only when every selected invoice has a chosen payment method.
   * Sole-method invoices auto-supply a hidden radio so they always pass.
   * Multi-method invoices require an explicit radio selection.
   */
  validateSubmitState() {
    if (!this.hasSubmitTarget) return;
    const hasSelections = this.selectedPatientInvoices.size > 0;
    // Facility invoices don't use patient payment methods, so they don't need a radio selection.
    const allHaveMethod = Array.from(this.selectedPatientInvoices.values()).every(
      (details) => details.payable_type === this.constructor.FACILITY_PAYABLE_TYPE || details.reuse_details_from_id
    );
    this.submitTarget.disabled = !hasSelections || !allHaveMethod;
    this.submitTarget.title =
      hasSelections && !allHaveMethod ? 'Select a payment method for each invoice.' : '';
  }

  /**
   * Intercepts filter/reset actions if the form has unsaved selections.
   */
  confirmIfDirty(event) {
    if (!window.isDirty) return;

    const message =
      event.currentTarget.dataset.confirmMessage ||
      'Applying filters will reload the page and you will lose your current selections and any entered payment data.\n\nDo you want to continue?';

    if (!window.confirm(message)) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }
}
